import Vue from "vue";
import Echo from "laravel-echo";
import axios from "axios";
import VueAxios from "vue-axios";
import store from "@/store";
import logout from "@/js/logout";
import { transactionBeep, messageBeep, profileVisitBeep } from "@/js/beep";
import { bus } from "@/bus";

window.Pusher = require("pusher-js");

export const echo = new Echo({
  broadcaster: "pusher",
  key: "pusher_key",
  authEndpoint: "/api/v1/broadcasting/auth",
  httpHost: window.location.hostname,
  wsHost: window.location.hostname,
  wsPort: 6001,
  forceTLS: false,
  disableStats: true
});

export const http = axios.create();

Vue.use(VueAxios, http);

http.interceptors.response.use(
  response => {
    return response;
  },
  error => {
    if (
      error.response &&
      (error.response.status === 401 || error.response.status === 419)
    ) {
      window.location = "/login?authExpired";
    } else {
      if (Vue.notify && error.response && error.response.data !== null) {
        if (typeof error.response.data !== "object") {
          Vue.notify({
            group: "errors",
            title: error.response.data,
            type: "error"
          });
        } else if (error.response.data.errors && error.response.data.message) {
          let errorMessages = error.response.data.errors;
          let text = "";
          for (let key in errorMessages) {
            errorMessages[key].forEach(errorText => {
              text += errorText + "<br>";
            });
          }

          Vue.notify({
            group: "errors",
            title:
              error.response.data.message === "The given data was invalid."
                ? "Es ist ein Fehler aufgetreten"
                : error.response.data.message,
            text: text,
            type: "error"
          });

          let errorMessage = "";
          if (error.response.data.errors.meta !== undefined) {
            errorMessage = error.response.data.errors.meta[0];
          } else {
            errorMessage = error.response.data.message;
          }
          store.commit("uploadProgress/error", errorMessage);
        } else if (error.response.data.message) {
          Vue.notify({
            group: "errors",
            title:
              error.response.data.message === "The given data was invalid."
                ? "Es ist ein Fehler aufgetreten"
                : error.response.data.message,
            type: "error"
          });
          store.commit("uploadProgress/error", error.response.data.message);
        } else if (error.response.status) {
          Vue.notify({
            group: "errors",
            title: `Ein Fehler ist aufgetreten. Code: ${error.response.status}`,
            type: "error"
          });
        }
      }
    }

    return Promise.reject(error);
  }
);

export function setCSRFToken(token) {
  // http.defaults.headers.common["X-CSRF-TOKEN"] = token;
  echo.connector.options.auth.headers["X-CSRF-TOKEN"] = token;
}

export function CSRFToken() {
  return document.querySelector('meta[name="csrf-token"][content]').content;
}

setCSRFToken(CSRFToken());

export function joinPrivateChannels() {
  const customerProfileId =
    store.getters["user/me"].customer_profile &&
    store.getters["user/me"].customer_profile.id;
  const roles = store.getters["user/me"].roles.map(role => role.name);

  if (roles.includes("customer")) {
    echo
      .private(`message.0.${customerProfileId}`)
      .listen("MessageWasCreated", event => {
        store.commit("user/addUnreadMessage", event);
        store.commit("message/update", [event]);
        let dialogPartner = event.sender;
        dialogPartner.last_message = event;
        store.commit("dialog/update", [dialogPartner]);
        if (store.getters["user/me"].settings.newMessageSound) {
          messageBeep();
        }
      })
      .listen("MessageWasUpdated", event => {
        store.commit("message/update", [event]);
      })
      .listen("MessageWasDeleted", event => {
        store.commit("message/delete", event.message_id);
      });

    echo
      .private(`customerProfile.${customerProfileId}`)
      .listen("TransactionWasCreated", transaction => {
        if (transaction.state !== 0) {
          return;
        }

        store.commit("balance/newTransaction", transaction);
        if (
          store.getters["user/me"].settings.flirtDollarSound &&
          transaction.amount > 0
        ) {
          transactionBeep();
        }
      })
      .listen("ProfileVisitEvent", () => {
        if (store.getters["user/me"].settings.profileVisitSound) {
          profileVisitBeep();
        }
      });

    echo
      .private(`user`)
      .listen("LogoutAllUsers", () => {
        logout("/login");
      })
      .listen("OnlineFakes", event => {
        Laravel.online = event;
        store.commit("online/updateOnline");
      });
  }

  echo
    .private(`user.${store.getters["user/me"].id}`)
    .listen("UserForceLogout", () => {
      logout("/login");
    })
    .listen("TransactionWasCreated", transaction => {
      if (transaction.state !== 0) {
        return;
      }

      store.commit("moderation/newTransaction", transaction);
      if (transaction.amount > 0) {
        transactionBeep();
      }
    })
    .listen("FriendRequestWasSent", event => {
      bus.$emit("friend-request-was-send", event);
    })
    .listen("MessageWasCreated", event => {
      store.commit("message/update", [event]);
      if (roles.includes("customer")) {
        messageBeep();
      }
    })
    .listen("MessageWasUpdated", event => {
      store.commit("message/update", [event]);
    })
    .listen("MessageWasDeleted", event => {
      store.commit("message/delete", event.message_id);
    })
    .listen("ModeratorReceivedDialogFromAdmin", data => {
      Vue.notify({
        title: `Admin ${data.name} hat dir den Dialog ${data.dialog_id} zugewiesen`,
        type: "success"
      });
    })
    .listen("ModeratorNotificationWasCreated", () => {
      bus.$emit("mod-notification");
    })
    .listen("TelegramWasVerified", event => {
      bus.$emit("telegram-was-verified", event.botName);
    });

  connectOnline();

  if (roles.includes("moderator")) {
    const modChannel = echo
      .join("moderator")
      .here(moderator => {
        store.commit("moderatorOnline/update", moderator);
      })
      .joining(moderator => {
        store.commit("moderatorOnline/update", moderator);
      })
      .leaving(moderator => {
        store.commit("moderatorOnline/delete", moderator);
      })
      .listenForWhisper("fresh-session", e => {
        if (e.id === store.getters["user/me"].id) {
          logout("/logout-wegen-mehreren-sessions");
        }
      })
      .listen("LogoutFromModeratorChat", data => {
        if (data.id === store.getters["user/me"].id) {
          http.post("/api/v1/moderation/end");
          logout("/logout-wegen-admin");
        }
      })
      .listen("LogoutAllModerators", () => {
        if (Laravel.config.logout_mods_on_socket_timeout) {
          http.post("/api/v1/moderation/end");
          logout("/logout-wegen-admin");
        }
      })
      .listen("WaitingRoomDisable", () => {
        Laravel.settings.enableWaitingRoom = false;
        bus.$emit("waiting-room-disable");

        // Hide waiting room link in navbar
        document.getElementById("navbar-modpanel-waitingroom").style.display =
          "none";
      });

    setTimeout(() => {
      modChannel.whisper("fresh-session", {
        id: store.getters["user/me"].id
      });
    }, 500);

    // leave chat on socket connection loss
    if (Laravel.config.logout_mods_on_socket_timeout) {
      echo.connector.pusher.connection.bind("unavailable", () => {
        setTimeout(() => {
          window.location.href = "/";
        }, 500);
      });
    }
  }

  if (roles.includes("admin")) {
    echo
      .private("admin")
      .listen("ModeratorMessageOutEvent", data => {
        bus.$emit("waiting-dialogs-update", data);
      })
      .listen("NewAdminTask", adminTask => {
        store.commit("adminBadges/newAdminTask", adminTask);
      })
      .listen("TreatedAdminTask", adminTask => {
        store.commit("adminBadges/treatedAdminTask", adminTask);
      })
      .listen("TableNewEntry", data => {
        store.dispatch("table/newEntry", data);
      })
      .listen("TableRemoveEntry", data => {
        let tableId = store.getters["table/tableByName"](data.tableName);
        if (tableId) {
          store.commit("table/removeModel", {
            tableId,
            modelId: data.entryId
          });
        }
      })
      .listen("BackgroundTaskUpdate", data => {
        store.commit("backgroundTask/update", data);
      })
      .listen("WaitingRoomDisable", () => {
        Laravel.settings.enableWaitingRoom = false;
        bus.$emit("waiting-room-disable");
      })
      .listen("CircularMailStatus", data => {
        store.commit("circularMailStatus/update", data);
      })
      .listen("ManualBackupDone", () => {
        store.commit("manualBackup/update", false);
      })
      .listen("InvoiceZipGenerated", data => {
        if (store.getters["user/me"].id == data.user_id) {
          const link = document.createElement("a");
          link.href = "/api/v1/exportInvoice/download?zipPath=" + data.zipPath;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      })
      .listen("TableDataFetched", data => {
        if (store.getters["user/me"].id == data.user_id) {
          store.commit(`table/${data.type}`, {
            models: data.result,
            count: data.result,
            id: data.tableId
          });
          bus.$emit("table-data-fetched", data.tableId);
        }
      });
  }

  if (roles.includes("circular_mail_writer")) {
    echo.private("circular_mail_writer").listen("CircularMailStatus", data => {
      store.commit("circularMailStatus/update", data);
    });
  }
}

export function connectOnline() {
  echo
    .join("online")
    .here(online => {
      store.commit("online/removeLeaver", online.id);
      store.commit("online/update", online);
    })
    .joining(online => {
      store.commit("online/removeLeaver", online.id);
      store.commit("online/update", online);
    })
    .leaving(({ id }) => {
      store.commit("online/addLeaver", id);
      setTimeout(() => {
        if (store.getters["online/leavers"].some(leaver => leaver === id)) {
          store.commit("online/delete", id);
        }
      }, 5000);
    });
}

export function disconnectOnline() {
  echo.leave("online");
}
